import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class ExpandingHeadNodeView extends NodeViewBase implements NodeView {
  private buttonDOM: HTMLButtonElement;
  private imgDOM: HTMLImageElement;
  private onButtonClicked: (event: MouseEvent) => void;

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Create the DOM elements
    this.contentDOM = this.dom.appendChild(document.createElement('mc-lwe-expandinghead-content'));
    this.buttonDOM = this.dom.appendChild(document.createElement('button'));
    this.buttonDOM.setAttribute('contenteditable', 'false');
    this.buttonDOM.setAttribute('draggable', 'false');
    this.buttonDOM.setAttribute('type', 'button');
    this.buttonDOM.classList.add('mc-lwe-expandinghead-button');
    this.imgDOM = this.buttonDOM.appendChild(document.createElement('img'));
    this.imgDOM.setAttribute('draggable', 'false');
    this.imgDOM.classList.add('mc-lwe-expandinghead-image');

    // Listen for button click events to fire the toggle event
    this.onButtonClicked = () => {
      // Dispatch a custom event to notify the expanding node view of the toggle
      this.dom.dispatchEvent(new CustomEvent('toggle-expanding', {
        bubbles: true,
        composed: true,
        cancelable: false
      }));
    };

    this.buttonDOM.addEventListener('click', this.onButtonClicked);

    this.addArrowKeyNavigation(this.buttonDOM);

    // Sync the node
    this.syncNode(node);
  }

  destroy() {
    this.buttonDOM.removeEventListener('click', this.onButtonClicked);
    super.destroy();
  }

  protected getArrowKeyTargetPos(pos: number, dir: -1 | 1, element: HTMLElement): number {
    const isOpen = this.dom.parentElement?.hasAttribute('mc-lwe-expanding-open');
    const isButtonOnLeft = this.buttonDOM === this.dom.firstChild;

    if (isButtonOnLeft) {
      // Button is on the left - content is to the right
      if (dir < 0) {
        // Navigate before the expanding container
        const $pos = this.editorView.state.doc.resolve(pos);
        return $pos.start($pos.depth) - 1;
      }

      // Navigate to the start of the expanding head content
      return pos + 1;
    }

    // Button is on the right - content is to the left
    if (dir < 0) {
      // Navigate to the end of the expanding head content (the text to the left of the button)
      return pos + this.node.nodeSize - 1;
    }

    // If the expanding body is open, navigate to the start of the expanding body
    if (isOpen) {
      return pos + this.node.nodeSize;
    }

    // Body is closed - navigate past the entire expanding container
    const $pos = this.editorView.state.doc.resolve(pos);
    return $pos.end($pos.depth) + 1;
  }

  stopEvent(event: Event): boolean {
    return this.buttonDOM?.contains(event.target as Node);
  }
}
